import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LandingGate from "@/pages/LandingGate";
import Legal from "@/pages/Legal";
import LegalUpdates from "@/pages/LegalUpdates";
import Login from "@/pages/Login";
import PasswordReset from "@/pages/PasswordReset";
import Pricing from "@/pages/Pricing";
import LegalWorkspace from "@/pages/LegalWorkspace";
import OwnerConsole from "@/pages/OwnerConsole";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );
}

function Protected({ children, ownerOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (ownerOnly && user.role !== "owner") return <Navigate to="/app" replace />;
  return children;
}

const PUBLIC_PAGES = ["/", "/legal", "/legal-updates", "/pricing", "/login"];

// Counts views of the public pages (no personal data; the server ignores the owner).
function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (PUBLIC_PAGES.includes(pathname)) api.post("/visit", { path: pathname }).catch(() => {});
  }, [pathname]);
  return null;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingGate />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/legal-updates" element={<LegalUpdates />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/app" element={<Protected><LegalWorkspace /></Protected>} />
      <Route path="/admin" element={<Protected ownerOnly><OwnerConsole /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <PageViewTracker />
          <AppRouter />
          <Toaster position="top-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
